import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { ThemeProvider } from "@emotion/react";
import theme from "../../theme";
import Logo from "../../assets/icons/logo.png";
import DashboardIcon from "../../assets/icons/DashboardIcon.png";
import LogoutIcon from "../../assets/icons/LogoutIcon.png";
import ProductIcon from "../../assets/icons/ListingIcon.png";
import OrderIcon from "../../assets/icons/order.png";
import AccountIcon from "../../assets/icons/accountIcon.png";
import PromoCodeIcon from "../../assets/icons/promoCode.png";
import SettingsIcon from "../../assets/icons/bracketIcon.png";
import DashboardPage from "../../pages/admin/dashboard/DashboardPage";
import ProductPage from "../../pages/admin/product/ProductPage";
import AccountManagementPage from "../../pages/admin/account/AccountManagementPage";
import AlertStockSettingsPage from "../../pages/admin/settings/AlertStockSettingsPage";
import _LogoutService from "../../service/LogoutService";
import { _AdminAuthorizeService } from "../../service/AuthorizeService";
import "./DrawerStyle.css";
import OrderStatus from "../../pages/common/OrderStatus";
import PromoCodePage from "../../pages/admin/promo/PromoCodePage";
const drawerWidthSm = 200;
const drawerWidthMd = 240;
const drawerWidthLg = 260;

const DrawerComponent = (props) => {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState("Dashboard");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    const role = _AdminAuthorizeService();
    setUserRole(role);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const imageMapping = {
    Dashboard: DashboardIcon,
    Product: ProductIcon,
    Account: AccountIcon,
    PromoCode: PromoCodeIcon,
    Order: OrderIcon,
    Settings: SettingsIcon,
    Logout: LogoutIcon,
  };

  const menuItems = ["Dashboard", "Product"];
  if (userRole === "Admin") {
    menuItems.push("Account");
  }
  menuItems.push("Order");
  menuItems.push("PromoCode");
  if (userRole === "Admin") {
    menuItems.push("Settings");
  }

  const handleMenuClick = (item, index) => {
    setSelectedItem(item);
    setSelectedIndex(index);
  };

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          paddingY: { xs: 2, sm: 2.5, md: 3 },
          borderBottom: `1px solid ${theme.palette.action.main}`,
        }}
      >
        <Box
          component="img"
          alt="Mandalay HW logo"
          src={Logo}
          sx={{
            width: { xs: 110, sm: 120, md: 140 },
            height: "auto",
            objectFit: "contain",
          }}
        />
      </Box>
      <List
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: { xs: 1, sm: 1.5 },
          overflowY: "auto",
        }}
      >
        {menuItems.map((text, index) => (
          <ListItem
            key={text}
            disablePadding
            onClick={() => handleMenuClick(text, index)}
            sx={{
              background:
                selectedItem === text
                  ? theme.palette.primary.main
                  : "transparent",
              color:
                selectedItem === text
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.main,
              borderRadius: "8px",
              marginX: { xs: "4px", sm: "8px" },
              marginBottom: { xs: "4px", sm: "6px" },
            }}
          >
            <ListItemButton
              sx={{
                py: { xs: 0.75, sm: 1.25 },
                px: { xs: 1, sm: 1.5 },
                gap: { xs: 0.5, sm: 1 },
                alignItems: "center",
              }}
            >
              <img
                src={imageMapping[text]}
                className={selectedItem === text ? "icon-invert" : "icon"}
                alt={`${text} Icon`}
              />
              <ListItemText
                primary={text}
                primaryTypographyProps={{
                  sx: {
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ mt: "auto" }}>
        <Divider />
        <List>
          <ListItem key="Logout" disablePadding onClick={_LogoutService}>
            <ListItemButton>
              <img
                src={imageMapping["Logout"]}
                className="icon"
                alt="Logout Icon"
              />
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const renderPage = () => {
    const pageMapping = {
      Admin: {
        0: <DashboardPage />,
        1: <ProductPage />,
        2: <AccountManagementPage />,
        3: <OrderStatus />,
        4: <PromoCodePage />,
        5: <AlertStockSettingsPage />,
      },
      User: {
        0: <DashboardPage />,
        1: <ProductPage />,
        2: <OrderStatus />,
        3: <PromoCodePage />,
      },
    };
    const userPages = pageMapping[userRole] || {};
    return userPages[selectedIndex] || <DashboardPage />;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: {
              xs: "100%",
              sm: `calc(100% - ${drawerWidthSm}px)`,
              md: `calc(100% - ${drawerWidthMd}px)`,
              lg: `calc(100% - ${drawerWidthLg}px)`,
            },
            ml: {
              xs: 0,
              sm: `${drawerWidthSm}px`,
              md: `${drawerWidthMd}px`,
              lg: `${drawerWidthLg}px`,
            },
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 2, sm: 3 },
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                color: theme.palette.primary.contrastText,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selectedItem}
            </Typography>
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{
            width: {
              sm: drawerWidthSm,
              md: drawerWidthMd,
              lg: drawerWidthLg,
            },
            flexShrink: { sm: 0 },
          }}
          aria-label="mailbox folders"
        >
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: { xs: "78vw", sm: drawerWidthSm },
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: {
                  sm: drawerWidthSm,
                  md: drawerWidthMd,
                  lg: drawerWidthLg,
                },
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            width: {
              sm: `calc(100% - ${drawerWidthSm}px)`,
              md: `calc(100% - ${drawerWidthMd}px)`,
              lg: `calc(100% - ${drawerWidthLg}px)`,
            },
          }}
        >
          <Toolbar />
          {renderPage()}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

DrawerComponent.propTypes = {
  window: PropTypes.func,
};

export default DrawerComponent;
